// @flow

import React, { Component, Fragment } from 'react';
import { compose } from 'redux';
import { get, isEqual } from 'lodash';

import JobDetailsPage from 'src/components/JobDetailsPage/JobDetailsPage';
import SendJobPage from 'src/components/SendJobPage/SendJobPage';
import { provideHooks } from 'redial';
import PageLoader from 'src/components/PageLoader/PageLoader';
import { connect } from 'react-redux';
import PageNotFound from 'src/components/PageNotFound/PageNotFound';
import ExpiredJob from 'src/components/ExpiredJob/ExpiredJob';
import { SafetyHelmet } from 'src/components/SafetyHelmet/SafetyHelmet';
import { getJobDetailsHead } from '@seek/seek-jobs-seo';
import uuidv4 from 'uuid/v4';

import {
  fetchJob,
  fetchJobApplication,
  jobDetailsPageLoaded,
  jobSelector
} from 'src/store/jobdetails/jobdetails';
import {
  savedJobSearch,
  saveJobAfterSignin
} from 'src/store/savedJobs/savedJobs';
import { getJobDetailLmis } from 'src/store/lmis/lmis';
import { getJobDetailFitMe } from 'src/store/fitme/fitme';

function extractIdFromPath(path: string) {
  const match = path.match(/^\/(job|expiredjob)\/(\d+)/i);
  return match ? match[2] : null;
}

let jobDetailsViewedCorrelationId;
const getJDVCorrelationId = () => {
  jobDetailsViewedCorrelationId = uuidv4();
};

const hooks = {
  first: () => {
    getJDVCorrelationId();
  },
  fetch: ({
    dispatch,
    path,
    userAgent,
    locale,
    requestId
  }: {
    dispatch: Function,
    path: string,
    userAgent: string,
    locale: Locale,
    requestId: string
  }) => {
    const id = extractIdFromPath(path);

    return dispatch(fetchJob(id, userAgent, locale, requestId));
  },
  defer: ({
    dispatch,
    path,
    authenticated
  }: {
    dispatch: Function,
    path: string,
    authenticated: boolean
  }) => {
    const id = extractIdFromPath(path);

    return dispatch(fetchJobApplication({ id, authenticated }));
  },
  authenticated: ({
    dispatch,
    routeEnter,
    path,
    handleIntent,
    locale,
    cookies,
    query
  }) => {
    const id = parseInt(extractIdFromPath(path), 10);

    if (!query.send) {
      dispatch(
        getJobDetailFitMe({
          jobId: id,
          locale,
          sessionId: cookies.JobseekerSessionId,
          jobDetailsViewedCorrelationId
        })
      );
    }

    if (routeEnter) {
      handleIntent('savejob', jobId => {
        const getJobFromState = ({ jobdetails }) => jobSelector(jobdetails);
        dispatch(saveJobAfterSignin(parseInt(jobId, 10), getJobFromState));
      });

      return dispatch(savedJobSearch(id));
    }

    return false;
  },
  mashup: ({ dispatch, requestId, locale }) => {
    if (locale === 'AU') {
      dispatch(getJobDetailLmis({ requestId }));
    }

    return false;
  },
  pageLoad: ({ dispatch, query }) =>
    dispatch(jobDetailsPageLoaded(query, jobDetailsViewedCorrelationId))
};

function expiredJob(status, pathname) {
  return (
    status &&
    status !== 'Active' &&
    (pathname && pathname.search('expiredjob') < 0)
  );
}

type Props = {
  job?: ChaliceJobDetail,
  jobApplication?: Object,
  locale: string,
  location: UrlLocation,
  authenticated: boolean,
  jobPending: boolean,
  userName?: string,
  userEmail: string
};

class JobDetails extends Component<Props> {
  shouldComponentUpdate(newProps) {
    return !isEqual(newProps, this.props);
  }

  render() {
    const { job, jobPending, location, userName, userEmail } = this.props;

    if (!job || !job.id) {
      return jobPending ? <PageLoader /> : <PageNotFound />;
    }

    if (expiredJob(job.status, location.pathname)) {
      return <ExpiredJob />;
    }

    const showSendJobPage = get(location, 'query.send', false);

    return (
      <Fragment>
        <SafetyHelmet {...getJobDetailsHead(job)} />
        {showSendJobPage ? (
          <SendJobPage {...{ userName, userEmail, jobId: job.id }} />
        ) : (
          <JobDetailsPage {...this.props} />
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = ({ jobdetails, location, user }) => ({
  location,
  locale: location.locale,
  job: jobdetails.result,
  authenticated: user.authenticated,
  jobPending: jobdetails.jobPending,
  seekerId: user.seekerId,
  sessionId: user.sessionId,
  userName: user.firstName,
  userEmail: user.emailAddress
});

export default compose(
  provideHooks(hooks),
  connect(mapStateToProps)
)(JobDetails);
